import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { 
  FaUsers, FaArrowLeft, FaHome, FaCalendarAlt, FaVideo, FaPhone, 
  FaUserTie, FaChevronLeft, FaChevronRight, FaClock, FaCheckCircle, 
  FaHourglass, FaPlus, FaExternalLinkAlt, FaFilter
} from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Session type icons/labels
const sessionTypeConfig = {
  video: { icon: FaVideo, label: 'Video Call', color: 'text-blue-600 bg-blue-50' },
  phone: { icon: FaPhone, label: 'Phone Call', color: 'text-green-600 bg-green-50' },
  in_person: { icon: FaUserTie, label: 'In Person', color: 'text-purple-600 bg-purple-50' }
};

// Status badges
const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: FaHourglass },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-800', icon: FaCheckCircle },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-800', icon: FaCheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: null }
};

// Calendar component
function CalendarView({ events, selectedDate, setSelectedDate, currentMonth, setCurrentMonth }) {
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  
  const getDayEvents = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };
  
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth.getMonth() === today.getMonth() && 
           currentMonth.getFullYear() === today.getFullYear();
  };
  
  const isSelected = (day) => {
    if (!selectedDate) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return selectedDate === dateStr;
  };
  
  const handleDayClick = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr === selectedDate ? null : dateStr);
  };
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  const days = [];
  // Add empty cells for days before the first day
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-24" />);
  }
  
  // Add cells for each day
  for (let day = 1; day <= daysInMonth; day++) {
    const dayEvents = getDayEvents(day);
    days.push(
      <div
        key={day}
        onClick={() => handleDayClick(day)}
        className={`h-24 border border-gray-100 p-1 cursor-pointer transition hover:bg-gray-50 ${
          isToday(day) ? 'bg-blue-50 border-blue-200' : ''
        } ${isSelected(day) ? 'ring-2 ring-blue-500' : ''}`}
        data-testid={`calendar-day-${day}`}
      >
        <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-blue-600' : 'text-gray-700'}`}>
          {day}
        </div>
        <div className="space-y-1">
          {dayEvents.slice(0, 2).map((event, idx) => {
            const TypeConfig = sessionTypeConfig[event.type] || sessionTypeConfig.video;
            return (
              <div
                key={idx}
                className={`text-xs px-1 py-0.5 rounded truncate ${TypeConfig.color}`}
                title={event.title}
              >
                {event.time} {event.professional_name}
              </div>
            );
          })}
          {dayEvents.length > 2 && (
            <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden" data-testid="calendar-view">
      {/* Calendar Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={prevMonth}
            className="p-2 hover:bg-white/10 rounded-lg transition"
            data-testid="prev-month-btn"
          >
            <FaChevronLeft />
          </button>
          <h3 className="text-xl font-bold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-white/10 rounded-lg transition"
            data-testid="next-month-btn"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
      
      {/* Days Header */}
      <div className="grid grid-cols-7 bg-gray-50 border-b">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days}
      </div>
    </div>
  );
}

// Consultation Card Component
function ConsultationCard({ consultation, onViewDetails }) {
  const TypeConfig = sessionTypeConfig[consultation.type] || sessionTypeConfig.video;
  const StatusConfig = statusConfig[consultation.status] || statusConfig.pending;
  const TypeIcon = TypeConfig.icon;
  const StatusIcon = StatusConfig.icon;
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  return (
    <div 
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition border-l-4 border-emerald-500"
      data-testid={`consultation-card-${consultation.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-bold text-gray-800">{consultation.professional_name}</h4>
          <p className="text-sm text-gray-500 capitalize">
            {consultation.professional_type?.replace('_', ' ')}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${StatusConfig.color}`}>
          {StatusIcon && <StatusIcon className="text-xs" />}
          <span>{StatusConfig.label}</span>
        </span>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-center space-x-2">
          <FaCalendarAlt className="text-gray-400" />
          <span>{formatDate(consultation.date)}</span>
        </div>
        <div className="flex items-center space-x-2">
          <FaClock className="text-gray-400" />
          <span>{consultation.time}</span>
        </div>
        <div className="flex items-center space-x-2">
          <TypeIcon className="text-gray-400" />
          <span>{TypeConfig.label}</span>
        </div>
        {consultation.family_name && (
          <div className="flex items-center space-x-2">
            <FaUsers className="text-gray-400" />
            <span>{consultation.family_name}</span>
          </div>
        )}
      </div>
      
      <div className="mt-4 pt-3 border-t flex justify-between items-center">
        <span className="text-xs text-gray-500">
          {consultation.participant_count} participant{consultation.participant_count !== 1 ? 's' : ''}
          {consultation.is_organizer && <span className="text-emerald-600 ml-2">(You're organizing)</span>}
        </span>
        <button
          onClick={() => onViewDetails(consultation)}
          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1"
        >
          <span>View</span>
          <FaExternalLinkAlt className="text-xs" />
        </button>
      </div>
    </div>
  );
}

export default function ConsultationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterType, setFilterType] = useState('all'); // all, as_organizer, as_participant
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/consultations/dashboard`, { withCredentials: true });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter events
  const filteredEvents = useMemo(() => {
    if (!dashboardData?.calendar_events) return [];
    
    let events = dashboardData.calendar_events;
    
    // Filter by type
    if (filterType === 'as_organizer') {
      events = events.filter(e => e.is_organizer);
    } else if (filterType === 'as_participant') {
      events = events.filter(e => !e.is_organizer);
    }
    
    // Filter by selected date
    if (selectedDate) {
      events = events.filter(e => e.date === selectedDate);
    }
    
    return events;
  }, [dashboardData, filterType, selectedDate]);
  
  const handleViewDetails = (consultation) => {
    navigate(`/resources?tab=consultations&consultation=${consultation.id}`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading consultation dashboard...</p>
        </div>
      </div>
    );
  }
  
  const stats = dashboardData?.stats || {};
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="consultation-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition" data-testid="logo-link">
                <BrandLogo className="w-9 h-9" testId="consultation-logo" />
                <span className="text-xl font-bold text-gray-800 hidden sm:inline">Legacy Forge Family</span>
              </Link>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800"
                data-testid="back-btn"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Consultation Schedule</h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <Link
                to="/resources"
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-xs sm:text-sm"
                data-testid="book-consultation-btn"
              >
                <FaPlus />
                <span className="hidden sm:inline">Book Consultation</span>
                <span className="sm:hidden">Book</span>
              </Link>
              <Link
                to="/"
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition text-xs sm:text-sm"
                data-testid="home-link"
              >
                <FaHome />
                <span>Home</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4">
            <p className="text-gray-500 text-sm">Total Sessions</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total || 0}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl shadow-md p-4 border border-emerald-200">
            <p className="text-emerald-600 text-sm">Upcoming</p>
            <p className="text-3xl font-bold text-emerald-700">{stats.upcoming || 0}</p>
          </div>
          <div className="bg-blue-50 rounded-xl shadow-md p-4 border border-blue-200">
            <p className="text-blue-600 text-sm">Organizing</p>
            <p className="text-3xl font-bold text-blue-700">{stats.as_organizer || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-xl shadow-md p-4 border border-purple-200">
            <p className="text-purple-600 text-sm">Invited To</p>
            <p className="text-3xl font-bold text-purple-700">{stats.as_participant || 0}</p>
          </div>
        </div>
        
        {/* Pending Responses Alert */}
        {stats.pending_responses > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaHourglass className="text-yellow-600" />
              <span className="text-yellow-800">
                You have <strong>{stats.pending_responses}</strong> consultation invite{stats.pending_responses !== 1 ? 's' : ''} awaiting your response
              </span>
            </div>
            <Link to="/resources?tab=consultations" className="text-yellow-700 hover:text-yellow-800 font-medium">
              Respond Now
            </Link>
          </div>
        )}
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar View */}
          <div className="lg:col-span-2">
            <CalendarView
              events={dashboardData?.calendar_events || []}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
            />
          </div>
          
          {/* Sidebar - Upcoming Sessions */}
          <div className="space-y-6">
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center space-x-2 mb-3">
                <FaFilter className="text-gray-400" />
                <span className="font-medium text-gray-700">Filter</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 rounded-full text-sm transition ${
                    filterType === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterType('as_organizer')}
                  className={`px-3 py-1.5 rounded-full text-sm transition ${
                    filterType === 'as_organizer' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Organizing
                </button>
                <button
                  onClick={() => setFilterType('as_participant')}
                  className={`px-3 py-1.5 rounded-full text-sm transition ${
                    filterType === 'as_participant' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Invited To
                </button>
              </div>
              
              {selectedDate && (
                <div className="mt-3 pt-3 border-t">
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear date filter ({selectedDate})
                  </button>
                </div>
              )}
            </div>
            
            {/* Session List */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-800 flex items-center space-x-2">
                <FaCalendarAlt className="text-emerald-600" />
                <span>
                  {selectedDate 
                    ? `Sessions on ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    : 'Upcoming Sessions'
                  }
                </span>
              </h3>
              
              {filteredEvents.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <FaCalendarAlt className="text-gray-300 text-4xl mx-auto mb-3" />
                  <p className="text-gray-500">
                    {selectedDate 
                      ? 'No consultations scheduled for this date'
                      : 'No upcoming consultations'
                    }
                  </p>
                  <Link
                    to="/resources"
                    className="inline-block mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
                  >
                    Book a Consultation
                  </Link>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {filteredEvents.map(event => (
                    <ConsultationCard
                      key={event.id}
                      consultation={event}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
